import React, { useState, useEffect, useMemo } from 'react';
import { Container, AppBar, Toolbar, Typography, Tabs, Tab, Box, Button, FormControl, InputLabel, Select, MenuItem, Backdrop, CircularProgress, Switch, FormControlLabel, Chip, Alert, IconButton, Dialog, DialogTitle, DialogContent, Table, TableHead, TableBody, TableRow, TableCell, TableContainer, Paper } from '@mui/material';
import LogoutIcon from '@mui/icons-material/Logout';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import DeepDive from './components/DeepDive';
import RestaurantLevel from './components/RestaurantLevel';
import Login from './components/Login';
import AllAccuracyDashboard from './components/AllAccuracyDashboard';
import { FolderProvider, useFolder } from './contexts/FolderContext';
import { isNewModelFormatFolder } from './utils/modelFormat';

function TabPanel(props) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`tabpanel-${index}`}
      aria-labelledby={`tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const MODEL_FACTOR_GROUPS = [
  {
    factorGroup: 'Lag Features (Historical Sales Signals)',
    whatWeConsider: ['Last 4 weeks – same day of week', 'Recent weekly average', 'Short-term trend (rising/falling sales)', 'Recent spikes or dips'],
    whyItMatters: 'Same-weekday history is the strongest predictor of near-term demand. If sales have been steadily increasing or declining, that direction is likely to persist. One-time spikes or dips are smoothed so the model does not overreact. This balances stability with momentum capture.',
  },
  {
    factorGroup: 'Seasonality (Month & Week-of-Month Patterns)',
    whatWeConsider: ['Month-level seasonality', 'Week-of-month patterns (start / mid / end)'],
    whyItMatters: 'Demand can shift across months and within the month due to attendance patterns and spending behavior. Early-, mid-, and month-end demand may differ structurally. Accounting for these patterns prevents systematic bias in forecasts.',
  },
  {
    factorGroup: 'Holiday Effects (Calendar Impact)',
    whatWeConsider: ['Major holidays (including lead & post days)', 'Minor holidays', 'Sandwich days'],
    whyItMatters: 'Holidays and long weekends impact cafeteria footfall. Demand may drop due to extended leave or shift around holiday periods. Adjusting for these effects ensures production planning reflects expected attendance rather than treating holidays as normal weekdays.',
  },
  {
    factorGroup: 'Item Components & Attributes',
    whatWeConsider: ['Veg / Non-Veg', 'Rice-based / Roti-based', 'Preparation style (fried, grilled, curry, etc.)', 'Cuisine type', 'Dessert / Sides / Beverage classification'],
    whyItMatters: 'Demand is often driven by specific components. Certain ingredients (e.g., paneer, soya) may consistently drive higher uptake, while other items may see lower demand. Component-level modeling captures these structural demand drivers and improves forecasts for new or low-volume items.',
  },
  {
    factorGroup: 'Session & Time-of-Day Patterns (Session Span)',
    whatWeConsider: ['Sale start time', 'Sale end time', 'Comparison with usual selling window'],
    whyItMatters: 'If an item ends earlier than usual, it may indicate stock-out and lost sales. If it starts late, peak demand may not be fully captured. Comparing session span with historical norms helps adjust for potential missed or incremental demand.',
  },
  {
    factorGroup: 'Cafeteria-Level Demand Trend',
    whatWeConsider: ['Foodcourt Lag', 'Restaurant Lag (at Veg/Non Veg Level)'],
    whyItMatters: 'Item demand generally moves in line with total cafeteria traffic. When overall footfall rises or declines, individual items are impacted accordingly. Aligning item forecasts with overall demand prevents isolated over- or under-estimation.',
  },
];

function DashboardContent({ onLogout }) {
  const [tabValue, setTabValue] = useState(0);
  const [selectedItemForDeepDive, setSelectedItemForDeepDive] = useState(null);
  const [restaurantFilters, setRestaurantFilters] = useState(null); // Store filters when navigating from RestaurantLevel
  const [modelInfoOpen, setModelInfoOpen] = useState(false);
  const { foldersByRunDate, runDates, selectedFolder, selectedRunDate, selectedModelDate, handleRunDateChange, handleModelDateChange, loading, refreshing, debugMode, setDebugMode, folderChangeError, setFolderChangeError } = useFolder();
  
  // Get username from localStorage
  const username = localStorage.getItem('username') || '';
  const isForecastUser = username === 'forecastuser';
  
  // Force debugMode to false if not forecastuser
  useEffect(() => {
    if (!isForecastUser && debugMode) {
      setDebugMode(false);
    }
  }, [isForecastUser, debugMode, setDebugMode]);
  
  // Persist selectedItemForDeepDive to localStorage
  useEffect(() => {
    if (selectedItemForDeepDive) {
      localStorage.setItem('selectedItemForDeepDive', JSON.stringify(selectedItemForDeepDive));
    } else {
      localStorage.removeItem('selectedItemForDeepDive');
    }
  }, [selectedItemForDeepDive]);
  
  // Restore selectedItemForDeepDive from localStorage on mount
  useEffect(() => {
    const savedItem = localStorage.getItem('selectedItemForDeepDive');
    if (savedItem && !selectedItemForDeepDive) {
      try {
        const parsedItem = JSON.parse(savedItem);
        setSelectedItemForDeepDive(parsedItem);
        setTabValue(1);
      } catch (e) {
        console.error('Error restoring selectedItemForDeepDive:', e);
      }
    }
  }, []); // Only run on mount
  
  // Persist restaurantFilters to localStorage
  useEffect(() => {
    if (restaurantFilters) {
      localStorage.setItem('restaurantFilters', JSON.stringify(restaurantFilters));
    } else {
      localStorage.removeItem('restaurantFilters');
    }
  }, [restaurantFilters]);
  
  // Restore restaurantFilters from localStorage on mount
  useEffect(() => {
    const savedFilters = localStorage.getItem('restaurantFilters');
    if (savedFilters && !restaurantFilters) {
      try {
        const parsedFilters = JSON.parse(savedFilters);
        setRestaurantFilters(parsedFilters);
      } catch (e) {
        console.error('Error restoring restaurantFilters:', e);
      }
    }
  }, []); // Only run on mount
  
  const restaurantTabIndex = 0;
  const deepDiveTabIndex = 1;

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    if (newValue !== deepDiveTabIndex) {
      setSelectedItemForDeepDive(null);
    } else if (newValue === deepDiveTabIndex && !selectedItemForDeepDive) {
      setRestaurantFilters(null);
    }
  };

  const handleItemClick = (itemData, filters) => {
    console.log('handleItemClick called with:', itemData, 'filters:', filters);
    setSelectedItemForDeepDive(itemData);
    setRestaurantFilters(filters);
    setTabValue(deepDiveTabIndex);
  };

  const handleBackToRestaurant = (filters) => {
    console.log('Navigating back to Restaurant Forecasts with filters:', filters);
    setRestaurantFilters(filters);
    setSelectedItemForDeepDive(null);
    setTabValue(restaurantTabIndex);
  };

  // Run date on/after the new-model cutoff (see utils/modelFormat.js)
  const isNewModelFormat = useMemo(
    () => isNewModelFormatFolder(selectedFolder),
    [selectedFolder]
  );

  // Format run date for display
  const formatRunDateDisplay = (runDateStr) => {
    if (!runDateStr || runDateStr.length !== 8) return runDateStr;
    return `${runDateStr.substring(0, 4)}-${runDateStr.substring(4, 6)}-${runDateStr.substring(6, 8)}`;
  };

  const formatModelDateDisplay = (modelDateStr) => {
    if (!modelDateStr) return '';
    if (modelDateStr === 'ALL') return 'All';
    if (modelDateStr.length !== 8) return modelDateStr;
    return `${modelDateStr.substring(0, 4)}-${modelDateStr.substring(4, 6)}-${modelDateStr.substring(6, 8)}`;
  };

  // Run dates for dropdown: when Predict Mode is on, show only the latest run date.
  const runDatesForDropdown = useMemo(() => {
    if (debugMode && runDates.length > 0) {
      return runDates.slice(0, 1); // runDates is latest-first, so first is latest
    }
    return runDates;
  }, [runDates, debugMode]);

  const modelDatesForRunDate = useMemo(() => {
    if (!selectedRunDate) return [];
    const list = foldersByRunDate[selectedRunDate] || [];
    if (debugMode && list.length > 0) {
      return list.slice(-1);
    }
    return list;
  }, [selectedRunDate, foldersByRunDate, debugMode]);

  // In Predict Mode with a single model week, auto-select it instead of All
  useEffect(() => {
    if (debugMode && selectedRunDate && modelDatesForRunDate.length === 1 && selectedModelDate === 'ALL') {
      handleModelDateChange(modelDatesForRunDate[0].modelDate);
    }
  }, [debugMode, selectedRunDate, modelDatesForRunDate, selectedModelDate, handleModelDateChange]);

  return (
    <div className="App" style={{ position: 'relative' }}>
      <AppBar position="static" elevation={1} sx={{ bgcolor: 'primary.main' }}>
        <Toolbar>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1, fontWeight: 500, display: 'flex', alignItems: 'center', gap: 0.5 }}>
            📊 Forecast Comparison Dashboard
            <IconButton
              size="small"
              onClick={() => setModelInfoOpen(true)}
              sx={{ color: 'white', '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' } }}
              aria-label="Model features info"
            >
              <InfoOutlinedIcon fontSize="small" />
            </IconButton>
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            {/* Debug Mode Toggle - Only visible for forecastuser */}
            {isForecastUser && (
              <FormControlLabel
                control={
                  <Switch
                    checked={debugMode}
                    onChange={(e) => setDebugMode(e.target.checked)}
                    disabled={refreshing}
                    sx={{
                      '& .MuiSwitch-switchBase.Mui-checked': {
                        color: 'white',
                      },
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                        backgroundColor: 'white',
                      },
                    }}
                  />
                }
                label={
                  <Typography sx={{ color: 'white', fontSize: '0.875rem' }}>
                    Predict Mode
                  </Typography>
                }
                sx={{ mr: 2 }}
              />
            )}
            <Box sx={{ display: 'flex', gap: 1.5, minWidth: 360, flexWrap: 'wrap', alignItems: 'center' }}>
              <FormControl size="small" sx={{ flex: 1, minWidth: 200, maxWidth: 260, bgcolor: 'rgba(255, 255, 255, 0.08)', borderRadius: 1 }}>
                <InputLabel sx={{ color: 'white', '&.Mui-focused': { color: 'white' } }}>Run Date</InputLabel>
                <Select
                  value={selectedRunDate || ''}
                  onChange={(e) => handleRunDateChange(e.target.value)}
                  label="Run Date"
                  renderValue={(value) => formatRunDateDisplay(value)}
                  disabled={refreshing}
                  sx={{ 
                    color: 'white',
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'rgba(255, 255, 255, 0.3)',
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'rgba(255, 255, 255, 0.5)',
                    },
                    '& .MuiSvgIcon-root': {
                      color: 'white',
                    },
                  }}
                >
                  {runDatesForDropdown.map((runDate) => (
                    <MenuItem key={runDate} value={runDate}>
                      {formatRunDateDisplay(runDate)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ flex: 1, minWidth: 200, maxWidth: 260, bgcolor: 'rgba(255, 255, 255, 0.08)', borderRadius: 1 }}>
                <InputLabel sx={{ color: 'white', '&.Mui-focused': { color: 'white' } }}>Model Date</InputLabel>
                <Select
                  value={selectedModelDate || ''}
                  onChange={(e) => handleModelDateChange(e.target.value)}
                  label="Model Date"
                  renderValue={(value) => (value === 'ALL' ? 'All' : formatModelDateDisplay(value))}
                  disabled={refreshing || !selectedRunDate}
                  sx={{ 
                    color: 'white',
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'rgba(255, 255, 255, 0.3)',
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'rgba(255, 255, 255, 0.5)',
                    },
                    '& .MuiSvgIcon-root': {
                      color: 'white',
                    },
                  }}
                >
                  {!debugMode && (
                    <MenuItem value="ALL">
                      All
                    </MenuItem>
                  )}
                  {modelDatesForRunDate.map((folder) => (
                    <MenuItem key={folder.modelDate} value={folder.modelDate}>
                      {formatModelDateDisplay(folder.modelDate)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </Box>
          <Button
            color="inherit"
            startIcon={<LogoutIcon />}
            onClick={onLogout}
            sx={{ ml: 2 }}
            disabled={refreshing}
          >
            Logout
          </Button>
        </Toolbar>
      </AppBar>

      <Dialog open={modelInfoOpen} onClose={() => setModelInfoOpen(false)} maxWidth="md" fullWidth>
        <DialogContent sx={{ pt: 3 }}>
          <Typography variant="h6" sx={{ mb: 1 }}>
            Forecast for Next Week – Overall Methodology
          </Typography>
          <Typography variant="body2" sx={{ mb: 1.5 }}>
            Sales are forecasted only for items that:
          </Typography>
          <Box component="ul" sx={{ m: 0, pl: 2.5, mb: 2, '& li': { mb: 0.25 } }}>
            <li><Typography variant="body2" component="span">Have more than 20 days of historical data</Typography></li>
            <li><Typography variant="body2" component="span">Have sold at least 50 units in the past month</Typography></li>
            <li><Typography variant="body2" component="span">Are part of the meal plan for next week</Typography></li>
          </Box>
          <Typography variant="body2" sx={{ mb: 2 }}>
            For these eligible items, the forecast combines historical sales behavior, calendar impact, external drivers, and item characteristics to estimate next week&apos;s demand in a structured and business-aligned manner.
          </Typography>
          <Typography variant="body2" sx={{ mb: 1, fontWeight: 600 }}>
            Forecast for Next Week is created based on the following features:
          </Typography>
          <TableContainer component={Paper} variant="outlined" sx={{ mb: 1 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600, bgcolor: 'action.hover', width: '22%' }}>Factor Group</TableCell>
                  <TableCell sx={{ fontWeight: 600, bgcolor: 'action.hover', width: '28%' }}>What We Consider</TableCell>
                  <TableCell sx={{ fontWeight: 600, bgcolor: 'action.hover' }}>Why It Matters for Forecast (Business Hypothesis)</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {MODEL_FACTOR_GROUPS.map((row, idx) => (
                  <TableRow key={idx}>
                    <TableCell sx={{ verticalAlign: 'top', fontWeight: 500 }}>{row.factorGroup}</TableCell>
                    <TableCell sx={{ verticalAlign: 'top' }}>
                      <Box component="ul" sx={{ m: 0, pl: 2, '& li': { mb: 0.25 } }}>
                        {row.whatWeConsider.map((item, i) => (
                          <li key={i}><Typography variant="body2" component="span">{item}</Typography></li>
                        ))}
                      </Box>
                    </TableCell>
                    <TableCell sx={{ verticalAlign: 'top' }}><Typography variant="body2" component="span">{row.whyItMatters}</Typography></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
      </Dialog>
      
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4, position: 'relative' }}>
        {folderChangeError && (
          <Alert 
            severity="error" 
            onClose={() => setFolderChangeError(null)}
            sx={{ mb: 2 }}
          >
            {folderChangeError}
          </Alert>
        )}
        {selectedModelDate !== 'ALL' ? (
          <>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={tabValue} onChange={handleTabChange} aria-label="dashboard tabs" disabled={refreshing}>
            <Tab label="🏪 Restaurant Forecasts" />
            <Tab label="🔍 Item Level Deep Dive" />
          </Tabs>
        </Box>
        
        <Box sx={{ 
          position: 'relative',
          minHeight: '400px',
          opacity: refreshing ? 0.5 : 1,
          pointerEvents: refreshing ? 'none' : 'auto',
          transition: 'opacity 0.2s ease-in-out',
          bgcolor: 'background.default'
        }}>
        <TabPanel value={tabValue} index={restaurantTabIndex}>
          <RestaurantLevel 
            onItemClick={handleItemClick} 
            initialFilters={restaurantFilters}
            onFiltersChange={setRestaurantFilters}
          />
        </TabPanel>
        
        <TabPanel value={tabValue} index={deepDiveTabIndex}>
          <DeepDive 
            initialSelectedItem={selectedItemForDeepDive} 
            onBackToRestaurant={handleBackToRestaurant}
            restaurantFilters={restaurantFilters}
            onItemUpdate={setSelectedItemForDeepDive}
          />
        </TabPanel>
        </Box>
          </>
        ) : (
          <Box sx={{ 
            position: 'relative',
            minHeight: '400px',
            opacity: refreshing ? 0.5 : 1,
            pointerEvents: refreshing ? 'none' : 'auto',
            transition: 'opacity 0.2s ease-in-out',
            bgcolor: 'background.default'
          }}>
            <AllAccuracyDashboard />
          </Box>
        )}
        
        {/* Loading Overlay */}
        <Backdrop
          open={refreshing}
          sx={{
            color: '#fff',
            zIndex: (theme) => theme.zIndex.drawer + 1,
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
          }}
        >
          <Box sx={{ textAlign: 'center' }}>
            <CircularProgress color="inherit" size={60} sx={{ mb: 2 }} />
            <Typography variant="h6" sx={{ mt: 2 }}>
              Refreshing data...
            </Typography>
            <Typography variant="body2" sx={{ mt: 1, opacity: 0.8 }}>
              Loading data from selected folder
            </Typography>
          </Box>
        </Backdrop>
      </Container>
    </div>
  );
}

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    // Check if user is already authenticated
    const authStatus = localStorage.getItem('isAuthenticated');
    if (authStatus === 'true') {
      setIsAuthenticated(true);
    }
  }, []);

  const handleLogin = (status) => {
    setIsAuthenticated(status);
  };

  const handleLogout = () => {
    localStorage.removeItem('isAuthenticated');
    localStorage.removeItem('username');
    setIsAuthenticated(false);
  };

  // Show login page if not authenticated
  if (!isAuthenticated) {
    return <Login onLogin={handleLogin} />;
  }

  // Show dashboard if authenticated
  return (
    <FolderProvider>
      <DashboardContent onLogout={handleLogout} />
    </FolderProvider>
  );
}

export default App;

